import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../utils/tpoGenQueryKeys';
import { sendNotification } from 'utils/helperMethods';
import type { UpdateComparisonReportPayload, ComparisonReportDetailResponse } from '../../types';

const updateComparisonReport = async (
    reportId: string,
    payload: UpdateComparisonReportPayload,
): Promise<ComparisonReportDetailResponse> => {
    let token = '';
    try { token = localStorage.getItem('accessToken') ?? ''; } catch {}
    const response = await axios(tpoGenUrls.updateComparisonReport(reportId), {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
        data: payload,
    });
    return response.data;
};

interface UpdateArgs {
    reportId: string;
    payload: UpdateComparisonReportPayload;
}

interface UseUpdateComparisonReportOptions {
    onSuccess?: (data: ComparisonReportDetailResponse) => void;
    successMessage?: string;
    errorMessage?: string;
}

const useUpdateComparisonReportMutation = ({
    onSuccess,
    successMessage = 'Report updated successfully',
    errorMessage = 'Failed to update comparison report',
}: UseUpdateComparisonReportOptions = {}) => {
    const queryClient = useQueryClient();

    const { mutate, isPending } = useMutation({
        mutationFn: ({ reportId, payload }: UpdateArgs) => updateComparisonReport(reportId, payload),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: tpoGenKeys.comparisonReports() });
            queryClient.invalidateQueries({ queryKey: tpoGenKeys.comparisonReportDetail(data.id) });
            sendNotification('success', successMessage);
            onSuccess?.(data);
        },
        onError: (error: unknown) => {
            const err = error as { response?: { data?: { detail?: unknown; message?: string } }; message?: string };
            const message = err?.response?.data?.detail;
            const resolved = typeof message === 'string' ? message
                : err?.response?.data?.message ?? err?.message ?? errorMessage;
            sendNotification('error', typeof resolved === 'string' ? resolved : errorMessage);
        },
    });

    return { mutate, isPending };
};

export default useUpdateComparisonReportMutation;
